import React from "react";
import { Bar } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
  PointElement,
} from "chart.js";
import ChartDataLabels from "chartjs-plugin-datalabels";

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
  PointElement,
  ChartDataLabels
);

const StackedBarChart = ({ stackedGraphData }) => {
  // --- Input Validation ---
  // 1. Check if stackedGraphData is valid and not empty
  if (!stackedGraphData || !Array.isArray(stackedGraphData) || stackedGraphData.length === 0) {
    return (
      <div className="text-sm text-red-400 text-center mt-10">
        No Data Available for This ARN
      </div>
    );
  }

  // 2. Validate the basic structure of each item in the array
  const isValidDataStructure = stackedGraphData.every(item =>
    typeof item === 'object' && item !== null && 'key' in item &&
    item.status_wise_count && typeof item.status_wise_count === 'object' &&
    Array.isArray(item.status_wise_count.buckets)
  );

  if (!isValidDataStructure) {
    return (
      <div className="text-sm text-red-400 text-center mt-10">
        No Data Avaialble for This ARN
      </div>
    );
  }
  // --- End Input Validation ---

  // Safely access data, now that validation has passed
  const parentCases = stackedGraphData.map((item) => item.status_wise_count.buckets); // No need for optional chaining here, as it's validated to exist

  const getCaseCounts = (status) =>
    parentCases.map((innerArray) => {
      const entry = innerArray.find((item) => item.key === status);
      return entry?.doc_count || 0;
    });

  const openCases = getCaseCounts("Open");
  const closeCases = getCaseCounts("Closed");
  const delayedCases = getCaseCounts("Delayed");
  const onTrackCases = getCaseCounts("On Track");
  const pendingCases = getCaseCounts("Pending");

  const labels = stackedGraphData.map((item) => item.key); // No need for optional chaining

  // Check if all data series are completely zero across all labels
  const hasAnyData =
    openCases.some(val => val > 0) ||
    closeCases.some(val => val > 0) ||
    delayedCases.some(val => val > 0) ||
    onTrackCases.some(val => val > 0) ||
    pendingCases.some(val => val > 0);

  if (!hasAnyData) {
      return (
          <div
              style={{
                  textAlign: "center",
                  padding: "2rem",
                  fontSize: "1.2rem",
                  color: "#666",
                  height: "100%",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
              }}
          >
              No meaningful data to display in the chart. All values are zero.
          </div>
      );
  }


  const data = {
    labels: labels,
    datasets: [
      {
        label: "Open",
        data: openCases,
        backgroundColor: "#a44b49",
      },
      {
        label: "Closed",
        data: closeCases,
        backgroundColor: "#4f6228",
      },
      {
        label: "Delayed",
        data: delayedCases,
        backgroundColor: "#2f5597",
      },
      {
        label: "On Track",
        data: onTrackCases,
        backgroundColor: "#f4b400",
      },
      {
        label: "Pending",
        data: pendingCases,
        backgroundColor: "#FF7900"
      }
    ],
  };

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      title: {
        display: true,
        text: "",
        font: {
          size: 18,
        },
      },
      legend: {
        position: "top",
        labels: {
          usePointStyle: true,
          pointStyle: "circle",
        },
      },
      datalabels: {
        display: true,
        color: "white",
        font: {
          weight: "bold",
        },
        formatter: (value) => {
          return value === 0 ? "" : value;
        },
      },
    },
    scales: {
      x: {
        stacked: true,
        grid: {
          display: false,
        },
        ticks: {
          autoSkip: false,
          autoSkipPadding: 0,
          maxRotation: 0,
          minRotation: 0,
          callback: function (value, index, ticks) {
            const label = this.getLabelForValue(value);
            if (typeof label === "string") {
              const maxCharsPerLine = 10;
              const words = label.split(" ");
              let lines = [];
              let currentLine = "";

              words.forEach((word) => {
                if (
                  (currentLine + (currentLine ? " " : "") + word).length >
                    maxCharsPerLine &&
                  currentLine.length > 0
                ) {
                  lines.push(currentLine);
                  currentLine = word;
                } else {
                  currentLine += (currentLine ? " " : "") + word;
                }
              });

              if (currentLine.length > 0) {
                lines.push(currentLine);
              }
              return lines.length > 0 ? lines : label;
            }
            return label;
          },
        },
      },
      y: {
        display: false,
        stacked: true,
        grid: {
          display: false,
          drawTicks: false,
          drawBorder: false,
        },
        ticks: {
          display: false,
        },
      },
    },
    layout: {
      padding: {
        bottom: 5,
      },
    },
  };
  const minBarWidthPerCategory = 80;

  const calculatedChartMinWidth = labels.length * minBarWidthPerCategory;

  const finalChartWidth = Math.max(calculatedChartMinWidth, 400);

  return (
    <div
      style={{
        overflowX: "auto",
        width: "100%",
        height: "90%",
      }}
    >
      <div
        style={{
          height: "100%",
          minWidth: `${finalChartWidth}px`,
        }}
      >
        <Bar data={data} options={options} />
      </div>
    </div>
  );
};

export default StackedBarChart;